/* eslint-disable react/jsx-props-no-spreading */
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import CoverDetailsDrawer from "@/components/molecules/quote-section/quote-cover/cover-details-drawer/cover-details-drawer";
import { ANNUAL_MULTI_TRIP, SINGLE_TRIP } from "@/constants";
import { CoverDrawerDetailsParent } from "@/schema/renewal-page/current-policy-page.schema";
import { CoverBenefitsType } from "@/schema/renewal-page/middle-panel.schema";
import { RootState } from "@/store/root-reducer";
import isCruiseImpact from "@/utils/check-cruise-impact-base-on-brand.utils";
import { getSeeMoreDetailsInfo } from "@/utils/get-cover-level-info";

import { BenefitsList } from "./benefits-list/benefits-list";
import Styles from "./current-policy-cover-bottom.module.css";

const UK_REGION_COUNTRY_CODES = ["GB", "IM", "GG", "JE"];
const UK_REGION_FROM_LOCATIONS = ["UK1", "UK3", "UK4", "UK5"];

type CurrentPolicyCoverBottomProps = {
  benefitsCopy?: CoverBenefitsType;
  benefitSeeMoreCta?: string;
  changeBtnLabel?: string;
  currentPolicy: string;
  coverDrawerDetails: CoverDrawerDetailsParent;
  changeCta?: string;
  cancellationCoverTitle: string;
  cancellationCoverDescription: string;
  cancellationDropdownLabel: string;
  cancellationCoverUpdateCta: string;
  cancellationCoverCancelCta: string;
};

export function CurrentPolicyCoverBottom({
  benefitsCopy,
  benefitSeeMoreCta,
  currentPolicy,
  coverDrawerDetails,
  ...props
}: Readonly<CurrentPolicyCoverBottomProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const { attributes } = useSelector((state: RootState) => state.quote);
  const { tripDetails } = attributes;

  const isUKRegionFromLocation = useMemo(() => {
    return (
      tripDetails?.fromLocation &&
      UK_REGION_FROM_LOCATIONS.includes(tripDetails.fromLocation)
    );
  }, [tripDetails]);

  const isUKRegionToLocationsOnly = useMemo(() => {
    const toLocationArr = tripDetails?.toLocation?.split(",") ?? [];
    return toLocationArr.every((substring) =>
      UK_REGION_COUNTRY_CODES.includes(substring)
    );
  }, [tripDetails]);

  const filteredDrawerDetails = useMemo(() => {
    const drawerData = getSeeMoreDetailsInfo(
      coverDrawerDetails,
      currentPolicy,
      tripDetails?.goingOnCruise && isCruiseImpact
    );

    const filterData = drawerData?.drawerData.filter(
      (detail) =>
        !(
          detail?.isExcludeUK &&
          isUKRegionFromLocation &&
          ((tripDetails?.area === "UK" &&
            tripDetails?.policyType === ANNUAL_MULTI_TRIP) ||
            (tripDetails?.policyType === SINGLE_TRIP &&
              isUKRegionToLocationsOnly))
        )
    );

    return {
      ...drawerData,
      drawerData: filterData,
    };
  }, [
    coverDrawerDetails,
    currentPolicy,
    tripDetails?.goingOnCruise,
    tripDetails?.area,
    tripDetails?.policyType,
    isUKRegionFromLocation,
    isUKRegionToLocationsOnly,
  ]);

  const shouldExcludeUKCountries = useMemo(() => {
    return (
      isUKRegionFromLocation &&
      ((tripDetails?.area === "UK" &&
        tripDetails?.policyType === ANNUAL_MULTI_TRIP) ||
        (tripDetails?.policyType === SINGLE_TRIP && isUKRegionToLocationsOnly))
    );
  }, [tripDetails, isUKRegionFromLocation, isUKRegionToLocationsOnly]);

  const filteredTableData = useMemo(() => {
    return shouldExcludeUKCountries
      ? benefitsCopy?.filter((data) => !data.isExcludeUK)
      : benefitsCopy;
  }, [shouldExcludeUKCountries, benefitsCopy]);

  return (
    <div
      className={` flex flex-col py-spacing-m pl-spacing-s pr-spacing-m
          ${currentPolicy.toLocaleLowerCase()}
          ${Styles.border}
        bg-surface-neutral-option-1`}
    >
      <div>
        <BenefitsList
          benefitsCopies={filteredTableData}
          isUKRegionFromLocation={isUKRegionFromLocation}
          isUKRegionToLocationsOnly={isUKRegionToLocationsOnly}
          currentPolicy={currentPolicy}
          cancellationModalData={props}
        />
      </div>
      <div className="self-start lg:self-center">
        <LabelButtonWrapper
          label={benefitSeeMoreCta ?? "See more details"}
          hierarchy="tertiary"
          className="font-body-font leading-normal font-[600] text-2xs py-spacing-xxs top-[-2px] !px-spacing-xxs"
          onClick={() => setIsOpen(true)}
        />
      </div>

      <CoverDetailsDrawer
        isOpen={isOpen}
        setOpen={(e) => setIsOpen(e)}
        {...filteredDrawerDetails}
      />
    </div>
  );
}
